import './journey.scss';

import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import { ContentHeader } from '@transferz/fe-component-library/src/components/contentHeader/ContentHeader';
import CircleXIcon from '@transferz/fe-component-library/src/components/icons/CircleXIcon';
import PenIcon from '@transferz/fe-component-library/src/components/icons/PenIcon';
import Notification from '@transferz/fe-component-library/src/components/notification/Notification';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { IUnresolvedAlert, JourneyStatusType, UnresolvedAlertType } from '@transferz/fe-models';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocalize } from 'localize-react';
import React, { useEffect, useState } from 'react';
import { useEventListener, useInterval } from 'usehooks-ts';
import { useLocation } from 'wouter';

import { Loading } from '../../components/loading/Loading';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { JOURNEY } from '../../queries/booking/bookingTypeDefs';
import { useMutationResolveAlert } from '../../queries/booking/hooks/useMutationResolveAlert';
import { useQueryJourney } from '../../queries/booking/hooks/useQueryJourney';
import { useQueryUnresolvedAlerts } from '../../queries/booking/hooks/useQueryUnresolvedAlerts';
import { useMutationPaymentResultCode } from '../../queries/payment/hooks/useMutationPaymentResultCode';
import { isJourneyEditable as isJourneyEditableFn } from '../../utils/journeyUtils';
import { clearCache, formatDate, futureDate, subtractDateInHours } from '../../utils/utils';
import { BookingStatus } from './components/bookingStatus/BookingStatus';
import { CancelBooking } from './components/cancelBooking/CancelBooking';
import { JourneyDetails } from './components/journeyDetails/JourneyDetails';
import { MoreInfoPopup } from './components/moreInfoPopup/MoreInfoPopup';

const Journey: React.FC<any> = ({ params }) => {
  const { translate, locale } = useLocalize();
  const flags = useFlags();
  const [location, setLocation] = useLocation();
  const [, , removeValue] = useLocalStorage('initAlertResolutionNotification');
  const [documentIsVisible, setDocumentIsVisible] = useState(true);
  const [unresolvedAlerts, setUnresolvedAlerts] = useState<any>([]);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [isMoreInfoPopupOpen, setIsMoreInfoPopupOpen] = useState(false);
  const [showAlertResolveNotification, setShowAlertResolveNotification] = useState(false);
  const [alertResolveNotificationMessage, setAlertResolveNotificationMessage] = useState('' as any);
  const [alertResolvenotificationVariant, setAlertResolvenotificationVariant] = useState('' as any);
  const urlParams: any = Object.fromEntries((new URLSearchParams(window.location.search)).entries());
  const phoneNumberVerified = urlParams['phone-number-verified'];
  const openCancelModal = urlParams?.openCancelModal;
  const token = urlParams.token;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { refetch: refetchJourney, data, loading, fetchMore } = useQueryJourney({
    variables: {
      id: Number(params.journeyId),
      bookingCode: params.bookingCode,
      includeCancellationDetails: true,
      includePickupTimeline: !!flags?.transferTimelineEnabled,
      language: locale,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const { refetch: getUnresolvedAlerts, loading: unresolvedAlertsLoading } = useQueryUnresolvedAlerts({
    variables: {
      params: {
        journeyId: 1,
      }, skip: true,
    },
  });

  const [getResultCode] = useMutationPaymentResultCode();
  const { status } = data?.journey || {};
  const allowedToModifyJourney = data?.journey?.travellerInfo?.allowedToModifyJourney;
  const isBookingCancellable = ((status === JourneyStatusType.Pending
    || status === JourneyStatusType.Confirmed
    || status === JourneyStatusType.Planned
  ) && allowedToModifyJourney);
  const [resolveAlert] = useMutationResolveAlert({ journeyId: Number(params.journeyId) });

  const customWidgetStyles = {
    '--page-background-color': window?.tz_globalConfigs?.styling?.pageBackgroundColor || '#fff',
    '--widget-background-color': window?.tz_globalConfigs?.styling?.widgetBackgroundColor || '#fc3',
    '--primary-button-color': window?.tz_globalConfigs?.styling?.primaryButtonColor || '#48a947',
  } as React.CSSProperties;

  const pickUpDate = data?.journey?.journeyExecutionInfo?.pickupDate;
  const pickUpTimeZone = data?.journey?.journeyExecutionInfo?.pickupTimeZone;
  const isBeforePickupTime = flags?.enableJourneyModification && futureDate(pickUpDate!, pickUpTimeZone!, 'hours') >= 24 && !loading;
  const isJourneyEditable = flags?.enableJourneyModification && !loading && isJourneyEditableFn(data?.journey);
  const canContactTransco = (!isBeforePickupTime
    && (status === JourneyStatusType.Pending || status === JourneyStatusType.Confirmed || status === JourneyStatusType.Planned));
  const canContactDriver = (!isBeforePickupTime
    && (status === JourneyStatusType.DriverUnderway || status === JourneyStatusType.DriverArrived || status === JourneyStatusType.JourneyInProgress));
  const editUntil = formatDate(`${subtractDateInHours(pickUpDate!, pickUpTimeZone!, 24)}`, 'DD-MM-YYYY HH:mm');

  useInterval(() => documentIsVisible && fetchMore({
    variables: {
      id: Number(params.journeyId),
      bookingCode: params.bookingCode,
      includeCancellationDetails: true,
      language: locale,
    },
    // @ts-ignore
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  }), Number(process.env.REACT_APP_REFRESH_INTERVAL ?? 15000));

  useEventListener('visibilitychange', () => {
    setDocumentIsVisible(!document.hidden);
  });

  useEffect(() => {
    if (isBookingCancellable && openCancelModal) {
      setIsPopupOpen(true);
    }
  }, [isBookingCancellable]);

  useEffect(() => {
    refetchJourney({
      variables: {
        id: Number(params.journeyId),
        bookingCode: params.bookingCode,
        includeCancellationDetails: true,
        language: locale,
      },
      update(cache: any) {
        clearCache(cache, JOURNEY, 'journey');
      },
    });
    if (urlParams.journeyChangeSuccess) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      setLocation(location);
      setNotificationVisible(true);
    }

    if (urlParams.redirectResult && urlParams?.requestId) {
      getResultCode({
        variables: {
          id: Number(urlParams?.requestId),
          params: {
            details: {
              redirectResult: urlParams.redirectResult,
            },
          },
        },
      })
        .then(async ({ data }: any) => {
          const { resultCode } = data?.paymentResultCode;
          if (resultCode !== 'Authorised' && resultCode !== 'Pending') {
            window.location.href = `${window.location.origin}${window.tz_globalConfigs.customUrlPrefix || ''}/mybooking/booking/${params?.bookingCode}/journeyChange/${urlParams?.changeId}/payment?resultCode=${resultCode}`;
          } else {
            setNotificationVisible(true);
          }
        });
    } else if (urlParams.resultCode) {
      setNotificationVisible(true);
    }
  }, [urlParams.journeyChangeSuccess, urlParams.redirectResult, urlParams.resultCode]);

  const handleGetUnresolvedAlerts = () => {
    setUnresolvedAlerts([]);
    if (isJourneyEditable) {
      getUnresolvedAlerts({
        params: {
          journeyId: Number(params.journeyId),
        },
        skip: false,
      })
        .then((response: any) => {
          const unresolvedAlertsData = [];
          if (response?.data?.unresolvedAlerts.length) {
            const incorrectPhoneAlert = response?.data?.unresolvedAlerts.find((alert: IUnresolvedAlert) => alert.type === UnresolvedAlertType.IncorrectTravellerPhone);
            const flightNumberALerts = response?.data?.unresolvedAlerts
              .filter((alert: IUnresolvedAlert) => {
                return alert.type === UnresolvedAlertType.FlightNumberNotFound
                  || alert.type === UnresolvedAlertType.FlightNumberIncorrectAirport
                  || alert.type === UnresolvedAlertType.FlightNumberNotProvided;
              });
            if (incorrectPhoneAlert) {
              unresolvedAlertsData.push({ journeyId: Number(params.journeyId), ...incorrectPhoneAlert, phone: data?.journey?.travellerInfo?.phone });
            }
            if (flightNumberALerts.length && flags?.showFlightNumberAlerts) {
              const hubIataCode = data?.journey?.hub?.details.filter((detail: any) => (detail.code === 'iata'))[0]?.value;
              unresolvedAlertsData.push({
                journeyId: Number(params.journeyId),
                ...flightNumberALerts[0],
                journeyCode: data?.journey?.journeyCode,
                flightNumber: data?.journey?.travellerInfo?.flightNumber,
                travellerInfo: data?.journey.travellerInfo,
                pickupDate: data?.journey?.journeyExecutionInfo?.pickupDate,
                isJourneyInbound: data?.journey?.originLocation?.hub,
                hubIataCode,
              });
            }
            setUnresolvedAlerts(unresolvedAlertsData);
          }
        });
    }
  };

  useEffect(() => {
    if (data && flags?.showUnresolvedAlerts && !phoneNumberVerified && !token) {
      handleGetUnresolvedAlerts();
    }
    if (phoneNumberVerified && token) {
      resolveAlert({
        variables: {
          token,
        },
      })
        .then(() => {
          setAlertResolvenotificationVariant(NotificationVariants.success);
          setAlertResolveNotificationMessage(translate('h_successfully_verified_phone'));
        })
        .catch(() => {
          setAlertResolvenotificationVariant(NotificationVariants.error);
          setAlertResolveNotificationMessage(translate('booking_somethingWentWrong'));
          handleGetUnresolvedAlerts();
        })
        .finally(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
          removeValue('initAlertResolutionNotification');
          setShowAlertResolveNotification(true);
        });
    }
  }, [data]);

  return (
    // hot fix for header btn, later will be implemented with story
    <div className={`journey white-background ${isBookingCancellable && (isJourneyEditable || canContactTransco || canContactDriver) ? 'custom-mb-24' : ''}`} style={customWidgetStyles}>
      {notificationVisible && <Notification
        variant={NotificationVariants.success}
        message={translate('booking_detailsSaveSuccessfully')}
        isSnackBar={true}
        closeIcon={true}
        onClose={() => setNotificationVisible(false)}
        className="journey-notification notification-width"
      />}
      {
        <ContentHeader
          bookingLink={`${window.tz_globalConfigs.customUrlPrefix || ''}/mybooking/booking/${params.bookingCode}`}
          bookingLinkText={translate('viewBooking')}
          title={translate('yourJourney')}
          bookingCode={data?.journey?.journeyCode}
        >
          <BookingStatus
            journey={data?.journey}
            classes="mb-24"
          />
          <div className="d-flex actions-container">
            {isJourneyEditable
              ? <Button
                className="edit-button"
                priority="primary"
                label={<>{translate('changeBooking')} <p>{translate('until')} {editUntil} </p></>}
                icon={<PenIcon direction="left" height={20} />}
                onClick={() => setLocation(`/booking/${params.bookingCode}/journey/${data?.journey.id}/edit`)}
                data-button-id="modify-journey"
                data-testid="button_modify-journey"
              />
              : <></>
            }
            {(canContactTransco || canContactDriver || !allowedToModifyJourney) && !loading && (
              <Button
                className="mb-16 more-info"
                priority="noPriority"
                variant="noVariant"
                textColor="asphalt7"
                moreText={translate('journey_moreInfo')}
                label={translate('journey_needToChange')}
                icon={<PenIcon direction="left" height={20} color="asphalt7" />}
                onClick={() => setIsMoreInfoPopupOpen(true)}
                data-button-id="more-info"
                data-testid="button_more-info"
              />
            )}
            {
              isBookingCancellable
                ? <Button
                  className="cancel-button"
                  textColor="red5"
                  icon={<CircleXIcon width={15} color="red5" />}
                  label={translate('header_cancelBookingBtn')}
                  priority="secondary"
                  onClick={() => setIsPopupOpen(true)}
                  data-button-id="cancel-journey"
                  data-testid="button_cancel-journey"
                />
                : <></>
            }
          </div>
        </ContentHeader>
      }
      <div className="journey-content width-w">
        {(loading || unresolvedAlertsLoading) && <Loading />}
        {(!loading && !unresolvedAlertsLoading) && (
          <>
            <CancelBooking
              params={params}
              isPopupOpen={isPopupOpen}
              setIsPopupOpen={setIsPopupOpen}
              data={data}
              refetchJourney={refetchJourney}
            />
            <MoreInfoPopup
              isPopupOpen={isMoreInfoPopupOpen}
              setIsPopupOpen={setIsMoreInfoPopupOpen}
              journey={data?.journey || {}}
            />
            <JourneyDetails
              unresolvedAlerts={unresolvedAlerts}
              unresolvedAlertsNotificationProps={{
                showAlertResolveNotification,
                alertResolveNotificationMessage,
                alertResolvenotificationVariant,
              }}
              journey={data?.journey || {}}
              params={params || {}}
              refetchJourney={refetchJourney}
              booking={data?.booking || {}}
              handleGetUnresolvedAlerts={handleGetUnresolvedAlerts}
              eligibility={{ gratuityRecomendations: data?.booking?.gratuityRecomendations, eligibleForGratuity: data?.booking?.eligibleForGratuity }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Journey;
